import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, Pagination, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function Tresorerie() {
    const navigate = useNavigate();
    const [solde, setSolde] = useState(null);
    const [evolution, setEvolution] = useState([]);
    const [operations, setOperations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [periode, setPeriode] = useState('mois');
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [annees, setAnnees] = useState([]);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchAnnees();
        fetchDonnees();
    }, [periode, annee]);

    const fetchAnnees = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stats.php?action=annees',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setAnnees(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement années", err);
        }
    };

    const fetchDonnees = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            
            // Récupérer le solde actuel
            const soldeResponse = await fetch(
                'https://sawd.julesferry-ga.com/api/finances.php?action=solde',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            // Récupérer l'évolution
            const evolutionResponse = await fetch(
                `https://sawd.julesferry-ga.com/api/finances.php?action=evolution&periode=${periode}&annee=${annee}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            // Récupérer les dernières opérations
            const operationsResponse = await fetch(
                `https://sawd.julesferry-ga.com/api/finances.php?action=operations&limite=20`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!soldeResponse.ok || !evolutionResponse.ok || !operationsResponse.ok) {
                throw new Error("Erreur chargement des données");
            }
            
            const soldeResult = await soldeResponse.json();
            const evolutionResult = await evolutionResponse.json();
            const operationsResult = await operationsResponse.json();
            
            setSolde(soldeResult.data);
            setEvolution(evolutionResult.data || []);
            setOperations(operationsResult.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const mois = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    // Pagination pour les opérations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentOperations = operations.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(operations.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        document.querySelector('.operations-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const maxValeur = evolution.length > 0 
        ? Math.max(...evolution.map(e => Math.max(e.entrees, e.sorties, Math.abs(e.solde || 0))), 1) 
        : 1;

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement de la trésorerie...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchDonnees}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))',
                                            boxShadow: '0 10px 20px rgba(16, 185, 129, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-money-euro-circle-line text-success" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Trésorerie</h1>
                                        <p className="text-muted mb-0">Suivi des flux financiers</p>
                                    </div>
                                </div>
                                <Button variant="outline-secondary" onClick={() => navigate('/stats/dashboard')}>
                                    <i className="ri-arrow-left-line me-2"></i>Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Solde actuel */}
                   {solde && (
    <Row className="g-3 mb-4 stats-row">
        {/* Entrées */}
        <Col md={4}>
            <Card className="border-0 shadow-sm h-100 stat-card">
                <Card.Body className="p-3 d-flex align-items-center">
                    <div className="stat-icon-wrapper bg-success-soft me-3">
                        <i className="ri-arrow-down-line text-success fs-4"></i>
                    </div>
                    <div>
                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                            Entrées
                        </small>
                        <h3 className="mb-0 fw-bold text-success">{formatCurrency(solde.entrees)}</h3>
                        <small className="text-muted">encaissements reçus</small>
                    </div>
                </Card.Body>
            </Card>
        </Col>

        {/* Sorties */}
        <Col md={4}>
            <Card className="border-0 shadow-sm h-100 stat-card">
                <Card.Body className="p-3 d-flex align-items-center">
                    <div className="stat-icon-wrapper bg-danger-soft me-3">
                        <i className="ri-arrow-up-line text-danger fs-4"></i>
                    </div>
                    <div>
                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                            Sorties
                        </small>
                        <h3 className="mb-0 fw-bold text-danger">{formatCurrency(solde.sorties)}</h3>
                        <small className="text-muted">décaissements effectués</small>
                    </div>
                </Card.Body>
            </Card>
        </Col>

        {/* Solde */}
        <Col md={4}>
            <Card className="border-0 shadow-sm h-100 stat-card">
                <Card.Body className="p-3 d-flex align-items-center">
                    <div className={`stat-icon-wrapper ${solde.solde >= 0 ? 'bg-primary-soft' : 'bg-warning-soft'} me-3`}>
                        <i className={`${solde.solde >= 0 ? 'ri-bank-line text-primary' : 'ri-alert-line text-warning'} fs-4`}></i>
                    </div>
                    <div>
                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                            Solde
                        </small>
                        <h3 className={`mb-0 fw-bold ${solde.solde >= 0 ? 'text-primary' : 'text-warning'}`}>
                            {formatCurrency(solde.solde)}
                        </h3>
                        <small className="text-muted">
                            {solde.solde >= 0 ? 'trésorerie positive' : 'découvert'}
                        </small>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    </Row>
)}



                    {/* Filtres */}
                    <Card className="filter-card border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-calendar-line me-2"></i>
                                            Période
                                        </Form.Label>
                                        <Form.Select 
                                            value={periode} 
                                            onChange={(e) => setPeriode(e.target.value)}
                                            className="filter-select py-2"
                                        >
                                            <option value="mois">📅 Mensuel</option>
                                            <option value="annee">📊 Annuel</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                {periode === 'mois' && (
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-muted small text-uppercase">
                                                <i className="ri-calendar-2-line me-2"></i>
                                                Année
                                            </Form.Label>
                                            <Form.Select 
                                                value={annee} 
                                                onChange={(e) => setAnnee(e.target.value)}
                                                className="filter-select py-2"
                                            >
                                                {annees.map(a => (
                                                    <option key={a} value={a}>{a}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}
                                <Col md={periode === 'mois' ? 4 : 8}>
                                    <Button 
                                        variant="primary" 
                                        onClick={fetchDonnees}
                                        className="w-100 py-2"
                                        style={{
                                            background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                            border: 'none',
                                            borderRadius: '10px'
                                        }}
                                    >
                                        <i className="ri-refresh-line me-2"></i>Actualiser
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
  
                    {/* Graphique d'évolution */}
                  
{evolution.length > 0 && (
    <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white py-3">
            <h5 className="mb-0">
                <i className="ri-bar-chart-2-line me-2 text-primary"></i>
                Évolution {periode === 'mois' ? 'mensuelle' : 'annuelle'}
            </h5>
        </Card.Header>
        <Card.Body>
            <div className="position-relative py-4" style={{ minHeight: '300px' }}>
                <div className="d-flex align-items-end justify-content-around h-100">
                    {evolution.map((item, index) => {
                        // ✅ Vérification sécurisée pour éviter l'erreur
                        const hauteurEntrees = maxValeur > 0 ? (item.entrees / maxValeur) * 150 : 0;
                        const hauteurSorties = maxValeur > 0 ? (item.sorties / maxValeur) * 150 : 0;
                        
                        // ✅ Formatage sécurisé du libellé
                        let libelle = '';
                        if (periode === 'mois') {
                            // Vérifier que item.mois existe et est dans la plage valide
                            const moisIndex = parseInt(item.mois) - 1;
                            if (!isNaN(moisIndex) && moisIndex >= 0 && moisIndex < mois.length) {
                                libelle = mois[moisIndex].substring(0, 3);
                            } else {
                                libelle = `Mois ${item.mois || '?'}`;
                            }
                        } else {
                            libelle = item.annee || 'N/A';
                        }
                        
                        return (
                            <div key={index} className="text-center" style={{ width: '50px' }}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip>
                                            Entrées: {formatCurrency(item.entrees || 0)}<br/>
                                            Sorties: {formatCurrency(item.sorties || 0)}
                                        </Tooltip>
                                    }
                                >
                                    <div className="d-flex justify-content-center gap-1 mb-2">
                                        <div 
                                            className="bg-success rounded-top"
                                            style={{ 
                                                height: `${hauteurEntrees}px`, 
                                                width: '15px',
                                                transition: 'height 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                        ></div>
                                        <div 
                                            className="bg-danger rounded-top"
                                            style={{ 
                                                height: `${hauteurSorties}px`, 
                                                width: '15px',
                                                transition: 'height 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                        ></div>
                                    </div>
                                </OverlayTrigger>
                                <small className="text-muted">{libelle}</small>
                            </div>
                        );
                    })}
                </div>
                <div className="d-flex justify-content-center gap-4 mt-4">
                    <div className="d-flex align-items-center">
                        <span className="badge bg-success me-2" style={{ width: '12px', height: '12px' }}></span>
                        <small>Entrées</small>
                    </div>
                    <div className="d-flex align-items-center">
                        <span className="badge bg-danger me-2" style={{ width: '12px', height: '12px' }}></span>
                        <small>Sorties</small>
                    </div>
                </div>
            </div>
        </Card.Body>
    </Card>
)}

                    {/* Dernières opérations */}
                    <Card className="border-0 shadow-sm operations-card">
                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="ri-history-line me-2 text-info"></i>
                                Dernières opérations
                            </h5>
                            <Badge bg="info" className="px-3 py-2">
                                {operations.length} opérations
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th>Référence</th>
                                            <th>Tiers</th>
                                            <th className="text-end" style={{minWidth:'200px'}}>Montant</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOperations.map((op, index) => (
                                            <tr key={index}>
                                                <td>{formatDate(op.date)}</td>
                                                <td>
                                                    <Badge 
                                                        bg={op.type === 'Règlement' || op.type === 'Facture' ? 'success' : 'danger'}
                                                        className="rounded-pill px-3 py-2"
                                                    >
                                                        {op.type}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <span className="fw-semibold">{op.reference}</span>
                                                </td>
                                                <td>{op.tiers}</td>
                                                <td className={`text-end fw-bold ${op.sens === 'entree' ? 'text-success' : 'text-danger'}`}>
                                                    {op.sens === 'entree' ? '+' : '-'}{formatCurrency(op.montant)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Card.Footer className="bg-white py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-muted small">
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, operations.length)} sur {operations.length} opérations
                                    </div>
                                    
                                    <Pagination className="mb-0">
                                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) pageNum = i + 1;
                                            else if (currentPage <= 3) pageNum = i + 1;
                                            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                            else pageNum = currentPage - 2 + i;
                                            
                                            return (
                                                <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => handlePageChange(pageNum)}>
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}
                                        
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            </Card.Footer>
                        )}
                    </Card>
                </div>

                <style>{`
                    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                    .fade-in { animation: fadeIn 0.5s ease; }
                    .filter-card { border-radius: 16px; background: #f8fafc; }
                    .filter-select { border-radius: 10px; border: 1px solid #e2e8f0; transition: all 0.2s ease; }
                    .filter-select:focus { border-color: #4361ee; box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1); }
                    .custom-table thead th { background: #f8fafc; color: #1e293b; font-weight: 600; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 1rem 0.75rem; }
                    .custom-table tbody td { padding: 1rem 0.75rem; vertical-align: middle; }
                    .custom-table tbody tr:hover { background: #f1f5f9 !important; }
                    .pagination .page-link { border-radius: 8px; margin: 0 2px; color: #4361ee; border: 1px solid #e2e8f0; }
                    .pagination .page-link:hover { background: #f1f5f9; color: #3a56d4; border-color: #cbd5e1; }
                    .pagination .active .page-link { background: linear-gradient(135deg, #4361ee, #3a56d4); border-color: #4361ee; color: white; }
               
        .stats-row .stat-card {
    transition: all 0.3s ease;
    border-radius: 16px;
    overflow: hidden;
}

.stats-row .stat-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
}

.stat-icon-wrapper {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    transition: transform 0.3s ease;
}

.stat-card:hover .stat-icon-wrapper {
    transform: scale(1.1);
}

.bg-success-soft {
    background: rgba(16, 185, 129, 0.1);
}

.bg-danger-soft {
    background: rgba(239, 68, 68, 0.1);
}

.bg-primary-soft {
    background: rgba(67, 97, 238, 0.1);
}

.bg-warning-soft {
    background: rgba(245, 158, 11, 0.1);
}

.bg-info-soft {
    background: rgba(13, 202, 240, 0.1);
}

.tracking-wide {
    letter-spacing: 0.5px;
}
               `}</style>

                <Footer />
            </div>
        </>
    );
}